import React, { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import { useAnalysisContext } from '../../context/AnalysisContext';

interface AdvancedOptionsDialogProps {
  open: boolean;
  onClose: () => void;
}

const PROMPT_KEYS: { key: string; label: string }[] = [
  { key: 'spelling-grammar', label: 'Spelling & Grammar' },
  { key: 'incomplete-info', label: 'Incomplete Information' },
  { key: 'title-content-mismatch', label: 'Title/Content Mismatch' },
  { key: 'chart-issues', label: 'Chart Visual Issues' },
  { key: 'visual-discrepancies', label: 'Visual Discrepancies' },
  { key: 'overall-deck-review', label: 'Overall Deck Review' },
  { key: 'overall-deck-analysis', label: 'Overall Deck Analysis' },
];

export const AdvancedOptionsDialog: React.FC<AdvancedOptionsDialogProps> = ({ open, onClose }) => {
  const {
    defaultPrompts,
    promptOverrides,
    loadDefaultPrompts,
    setPromptOverride,
  } = useAnalysisContext();

  const [tabIndex, setTabIndex] = React.useState(0);

  useEffect(() => {
    if (open && !defaultPrompts) {
      loadDefaultPrompts();
    }
  }, [open, defaultPrompts, loadDefaultPrompts]);

  const prompts = useMemo(() => {
    return { ...(defaultPrompts || {}), ...(promptOverrides || {}) } as Record<string, string>;
  }, [defaultPrompts, promptOverrides]);

  const handleChange = (key: string, value: string) => {
    setPromptOverride(key, value);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      onClick={(e) => e.stopPropagation()}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <DialogTitle onClick={(e) => e.stopPropagation()}>Advanced Options</DialogTitle>
      <DialogContent dividers onClick={(e) => e.stopPropagation()}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Override the prompts used during analysis. These overrides will be saved with this deck only.
        </Typography>

        <Tabs
          value={tabIndex}
          onChange={(e, idx) => {
            e.preventDefault();
            e.stopPropagation();
            setTabIndex(idx);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {PROMPT_KEYS.map((p) => (
            <Tab 
              key={p.key} 
              label={p.label}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          ))}
        </Tabs>

        {PROMPT_KEYS.map((p, idx) => (
          <Box key={p.key} role="tabpanel" hidden={tabIndex !== idx}>
            {tabIndex === idx && (
              <TextField
                label={`${p.label} Prompt`}
                value={prompts[p.key] || ''}
                onChange={(e) => handleChange(p.key, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                fullWidth
                multiline
                minRows={12}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions onClick={(e) => e.stopPropagation()}>
        <Button 
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedOptionsDialog;


